import { styled } from "@linaria/react";
import React from "react";
import t from "../../../../../i18n";
import type { DependencyTree } from "../modalForm/ConfirmDelete";
import { mapToExportFormat } from "../modalForm/ConfirmDelete";
import { color, fonts } from "@ui";
import { DownloadLine, AlertFill } from "@ui/IconV2/react";
import type { Setup } from "../../../../../api/useSetups";
import Button from "@ui/Button";
import { colors, spacings } from "@ui/Theme";
import type { Quantity } from "../../../../../api/useQuantities";
import SmallCollapseEditChanges from "./SmallCollapseEditChanges";

/* ─────────── styled helpers ─────────── */

const GlobalWrapper = styled.div`
  background-color: ${color.phosphorus50};
  padding: ${spacings.xs};
  border-top: 1px solid ${colors.titanium200};
`;

const StyledButton = styled(Button)`
  font: ${fonts.body2};
  display: flex;
  align-items: center;
  gap: ${spacings.xs};
`;

const DetailNote = styled.div`
  margin: ${spacings.s} 0;
`;

const Content = styled.div`
  font: ${fonts.body1};
`;

const ImpactCollapseBody = styled.div`
  spacing: ${spacings.m};
`;

const PrimaryList = styled.ul`
  list-style-type: initial;
  margin-left: ${spacings.m};

  li {
    font: ${fonts.body3};

    span {
      font: ${fonts.body1};
    }
  }
`;

/* ─────────── types ─────────── */

interface P {
  setups: Setup[];
  formulaDependencies: DependencyTree;
  quantities: Quantity[];
  hasImpactOnMetrology: boolean;
  hasImpactOnFormula: boolean;
}

/* ─────────── helpers ─────────── */

const flattenDependencyTreeIds = (id: string, tree: DependencyTree["trees"]): string[] => [
  id,
  ...tree.flatMap(c => flattenDependencyTreeIds(c.id, c.trees)),
];

/* ─────────── component ─────────── */

const WarningEdit = ({
  setups,
  formulaDependencies,
  quantities,
  hasImpactOnMetrology,
  hasImpactOnFormula,
}: P) => {
  /* state */
  const [expanded, setExpanded] = React.useState(false); // inner collapse
  const [impactExpanded, setImpactExpanded] = React.useState(false); // outer collapse

  // Debug logs
  React.useEffect(() => {
    console.debug("WarningEdit component rendered with props:", {
      formulaDependenciesId: formulaDependencies.id,
      formulaDependenciesName: formulaDependencies.name,
      hasTreeData: formulaDependencies.trees && formulaDependencies.trees.length > 0,
      hasImpactOnMetrology,
      hasImpactOnFormula,
    });
  }, [formulaDependencies, hasImpactOnMetrology, hasImpactOnFormula]);

  /* first-level dependencies */
  const dependenciesLevel1 = formulaDependencies.trees.map(c => c.name);
  const isUsedInOtherFormula = dependenciesLevel1.length > 0;

  /* full dependency-graph ids */
  const dependencyTreeQuantityIds = React.useMemo(
    () => flattenDependencyTreeIds(formulaDependencies.id, formulaDependencies.trees),
    [formulaDependencies.id, formulaDependencies.trees],
  );

  /* simulation / diagnosis inputs across all setups */
  const [simulationInputQuantityIds, diagnosisInputQuantityIds] = React.useMemo(
    () =>
      setups
        .map(setup => [
          setup.simulationInputs.map(({ quantityId }) => quantityId),
          setup.diagnosisInputs
            .filter(({ enabled }) => enabled)
            .map(({ quantityId }) => quantityId),
        ])
        .reduce<[string[], string[]]>(
          (acc, [simIds = [], diagIds = []]) => {
            acc[0] = [...acc[0], ...simIds];
            acc[1] = [...acc[1], ...diagIds];
            return acc;
          },
          [[], []],
        ),
    [setups],
  );

  const dependenciesAreSimulationInput = React.useMemo(
    () => simulationInputQuantityIds.some(id => dependencyTreeQuantityIds.includes(id)),
    [dependencyTreeQuantityIds, simulationInputQuantityIds],
  );

  const dependenciesAreDiagnosisInput = React.useMemo(
    () => diagnosisInputQuantityIds.some(id => dependencyTreeQuantityIds.includes(id)),
    [dependencyTreeQuantityIds, diagnosisInputQuantityIds],
  );

  /* tree traversal helpers */
  const getChildren = React.useCallback(
    (quantity: string) => {
      const stack = [formulaDependencies];
      while (stack.length > 0) {
        const node = stack.pop();
        if (node?.name === quantity) return node.trees;
        if (node?.trees) stack.push(...node.trees);
      }
      return [];
    },
    [formulaDependencies],
  );

  const getChildrenCount = React.useCallback((node: DependencyTree) => {
    const stack = [...node.trees];
    let count = 0;
    while (stack.length > 0) {
      const cur = stack.pop();
      count += 1;
      if (cur?.trees) stack.push(...cur.trees);
    }
    return count;
  }, []);

  /* updated-quantities count (whole tree) */
  const updatedQuantitiesCount = React.useMemo(() => {
    let count = 0;
    const stack = [formulaDependencies];
    while (stack.length > 0) {
      const n = stack.pop();
      if (n?.trees) stack.push(...n.trees);
      count += 1;
    }
    return count;
  }, [formulaDependencies]);

  /* JSON export */
  const dependenciesJsonified = React.useMemo(
    () => mapToExportFormat(formulaDependencies, quantities),
    [formulaDependencies, quantities],
  );

  const handleDownload = React.useCallback(() => {
    const content = JSON.stringify(dependenciesJsonified, null, 4);
    const a = document.createElement("a");
    a.setAttribute("href", URL.createObjectURL(new Blob([content], { type: "application/json" })));
    a.setAttribute("download", `${new Date().toISOString()}_details_of_impacted_quantities.json`);
    a.click();
  }, [dependenciesJsonified]);

  /* whether any impacts exist */
  const hasAnyImpact =
    dependenciesAreSimulationInput ||
    dependenciesAreDiagnosisInput ||
    isUsedInOtherFormula ||
    hasImpactOnFormula ||
    hasImpactOnMetrology;

  /* ─────────── render ─────────── */
  return (
    <GlobalWrapper>
      <Content>
        {/* outer collapse */}
        {hasAnyImpact && (
          <>
            <SmallCollapseEditChanges
              title={t("project.settings.quantity.side_panel.edition.impacts_warning")}
              expanded={impactExpanded}
              handleClick={() => setImpactExpanded(prev => !prev)}
              icon={<AlertFill size="s" color={color.phosphorus500} />}
            >
              <ImpactCollapseBody>
                {/* leading sentence */}
                <div>{t("project.quantities.edit.impacts.body")}</div>

                {/* main impacts list */}
                <PrimaryList>
                  {(hasImpactOnFormula || hasImpactOnMetrology) && (
                    <li>
                      <span>
                        {t("project.quantities.edit.formula.body.evaluation_relaunch_by_type", {
                          hasImpactOnFormula,
                          hasImpactOnMetrology,
                        })}
                      </span>
                    </li>
                  )}

                  {(hasImpactOnFormula || hasImpactOnMetrology) && isUsedInOtherFormula && (
                    <li>
                      <span>
                        {t("project.quantities.edit.formula.body.impacted_quantities", {
                          impactedQuantitiesCount: updatedQuantitiesCount - 1,
                        })}
                      </span>
                    </li>
                  )}

                  {dependenciesAreSimulationInput && (
                    <li>
                      <span>{t("project.quantities.edit.simulation.impacting.quantity")}</span>
                    </li>
                  )}

                  {!dependenciesAreSimulationInput && dependenciesAreDiagnosisInput && (
                    <li>
                      <span>{t("project.quantities.edit.simulation.impacting.diagnostic")}</span>
                    </li>
                  )}
                </PrimaryList>

                {/* nested collapse for per-quantity details */}
                {isUsedInOtherFormula && (
                  <>
                    <SmallCollapseEditChanges
                      title={t("project.quantities.edit.details.impacted_quantities")}
                      expanded={expanded}
                      handleClick={() => setExpanded(prev => !prev)}
                    >
                      <PrimaryList>
                        {dependenciesLevel1.map(dep => (
                          <li key={dep}>
                            <span>
                              {t("project.quantities.edit.formula.details.label.level1", {
                                quantityName: dep,
                                dependencyCount: getChildren(dep).length,
                              })}
                            </span>

                            {/* second level */}
                            <PrimaryList>
                              {getChildren(dep).map(child => (
                                <li key={child.id}>
                                  <span>
                                    {t("project.quantities.edit.formula.details.label.level2", {
                                      quantityName: child.name,
                                      dependencyCount: getChildrenCount(child),
                                    })}
                                  </span>
                                </li>
                              ))}
                            </PrimaryList>
                          </li>
                        ))}
                      </PrimaryList>

                      <DetailNote>
                        {t("project.quantities.edit.formula.nota", {
                          quantityName: formulaDependencies.name,
                        })}
                      </DetailNote>
                    </SmallCollapseEditChanges>

                    {/* JSON download */}
                    <StyledButton
                      variant="link"
                      onClick={handleDownload}
                      leftIcon={<DownloadLine size="s" />}
                    >
                      {t("project.quantities.edit.formula.download.details_impacted_quantities")}
                    </StyledButton>
                  </>
                )}
              </ImpactCollapseBody>
            </SmallCollapseEditChanges>
          </>
        )}
      </Content>
    </GlobalWrapper>
  );
};

export default React.memo(WarningEdit);
